import { useEffect, useState } from 'react';
import { FaUser } from 'react-icons/fa';
import { GoSync } from 'react-icons/go';
import { FiEdit3, FiTrash } from 'react-icons/fi';
import { toastSuccess } from '../utils/toasts';
import FilterTable, { BaseData } from './Table/Table';
import EmptyTable from './Table/EmptyTable';
import CustomModal from './atoms/CustomModal';
import Button from './atoms/Button';
import { BankAccountDTO } from '../models/bank-account';
import CreateEditBankAccount from './CreateEditBankAccount';
import { useDeleteBankAccountMutation } from '../store';
import useGetParams from '../hooks/useGetParams';
import DollarSign from './icons/DollarSign';

interface BankAccountsTableProps {
  data: BankAccountDTO[];
}

interface BankAccount extends BaseData {
  id: string;
  status: string | JSX.Element;
  account_holder: string | JSX.Element;
  account_number: string | JSX.Element;
  bank: string | JSX.Element;
}

const BankAccountsTable = ({ data }: BankAccountsTableProps) => {
  const [modalBody, setModalBody] = useState<React.ReactNode>(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteBankAccount, resDeleteBankAccount] = useDeleteBankAccountMutation();
  const { businessId } = useGetParams();
  const config = [
    {
      id: 'status',
      label: 'Estado',
    },
    {
      id: 'account_holder',
      label: 'Titular de la cuenta',
    },
    {
      id: 'account_number',
      label: 'CBU/CVU o alias',
    },
    {
      id: 'bank',
      label: 'Banco',
    },
  ];

  useEffect(() => {
    if (resDeleteBankAccount.isSuccess) {
      toastSuccess('Cuenta eliminada');
    }
  }, [resDeleteBankAccount.isSuccess]);

  const handleCreateClick = () => {
    setModalBody(
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl font-medium">Agregar cuenta</h1>
          <p className="text-sm text-text-secondary">
            Debes cargar los datos de la cuenta que quieres dar de alta.
            Verifica que los datos sean correctos.
          </p>
        </div>
        <CreateEditBankAccount onSuccess={() => setShowModal(false)} />
      </div>,
    );
    setShowModal(true);
  };

  const handleEditClick = (row: BankAccount) => {
    setModalBody(
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl font-medium">Editar cuenta</h1>
          <p className="text-sm text-text-secondary">
            Debes cargar los datos de la cuenta que quieres dar de alta.
            Verifica que los datos sean correctos.
          </p>
        </div>
        <CreateEditBankAccount
          onSuccess={() => setShowModal(false)}
          bankAccount={data.find((item: BankAccountDTO) => item.id === row.id)}
        />
      </div>,
    );
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    deleteBankAccount({ businessId, id });
    setShowModal(false);
  };

  const handleDeleteClick = (row: BankAccount) => {
    setModalBody(
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-medium">Eliminar cuenta</h1>
        <p className="text-sm text-text-secondary">
          ¿Estás seguro que deseas eliminar esta cuenta?
        </p>
        <div className="flex flex-row gap-2">
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" loading={resDeleteBankAccount.isLoading} onClick={() => handleDelete(row.id)}>
            Eliminar
          </Button>
        </div>
      </div>,
    );
    setShowModal(true);
  };

  const actions = (row: BankAccount) => (
    <div className="flex gap-2">
      <FiEdit3 onClick={() => handleEditClick(row)} className='cursor-pointer w-5 h-5' />
      {resDeleteBankAccount.isLoading ? (
        <div className="animate-spin">
          <GoSync className="animate-spin" />
        </div>
      ) : (
        <FiTrash onClick={() => handleDeleteClick(row)} className='cursor-pointer w-5 h-5' />
      )}
    </div>
  );

  const rows: BankAccount[] = data
    .slice()
    .sort((a, b) => {
      // First sort by status (active first, then inactive)
      if (a.status !== b.status) {
        return a.status === 'active' ? -1 : 1;
      }
      // Then sort by creation date within each status group
      return (
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    })
    .map((item) => ({
      id: item.id,
      real_status: item.status,
      status:
        item.status === 'active' ? (
          <p className="tag-primary">
            Activa
          </p>
        ) : (
          <p className="tag-warning">
            No activa
          </p>
        ),
      account_holder: <p className="whitespace-nowrap ">{item.holder_name}</p>,
      account_number: <p className="whitespace-nowrap">{item.transfer_code}</p>,
      bank: <p className="whitespace-nowrap">{item.entity_name}</p>,
    }));

  const customButton = (
    <Button onClick={handleCreateClick} variant="secondary">
      <div className="flex flex-row gap-2 items-center">
        <DollarSign className='text-xl' />
        <p className="whitespace-nowrap hidden md:block">Agregar cuenta</p>
      </div>
    </Button>
  );

  return (
    <div>
      {data.length > 0 ? (
        <FilterTable
          rows={rows}
          headCells={config}
          actions={actions}
          pagination={{ pageSize: 10, enabled: true }}
          customButtons={[customButton]}
          tableId="bank-accounts-table"
        />
      ) : (
        <EmptyTable
          title="No hay cuentas declaradas"
          info="Agrega una cuenta para poder declarar tus liquidaciones"
          icon={FaUser}
          iconColor="text-secondary-500"
          button={[
            {
              label: 'Agregar cuenta',
              onClick: handleCreateClick,
              reactIcon: <DollarSign className='text-xl' />,
              variant: 'secondary',
            },
          ]}
        />
      )}
      <CustomModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        hideCancelButton
      >
        {modalBody}
      </CustomModal>
    </div>
  );
};

export default BankAccountsTable;
